import React, { FC, useMemo, useState } from 'react'
import { Box, Button, DialogActions, Grid, IconButton, Typography } from '@mui/material'
import { Edit as EditIcon, Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material'
import { Link } from 'react-router-dom'
import { DataLoad, Dropdown, HasSidebarLayout, ListHeader, StatefulDialog } from 'src/components'
import { ReleaseCard } from 'src/components/sections/release'
import { useConfigurationListRequest, useEditConfigurationParamRequest, useReleaseListRequest } from 'src/hooks/api'
import { ConfigurationParameter, Release } from 'src/api'
import { useAddPopupMessage, useDialog, useDropdownItemFactory } from 'src/hooks/ui'
import { DialogNames } from 'src/enums'

interface ReleaseListProps {
  releases: Release[]
  configParamLatestAvailableRelease?: ConfigurationParameter
  configParamMinimumAppVersion?: ConfigurationParameter
}

const ReleaseList: FC<ReleaseListProps> = ({ releases, configParamLatestAvailableRelease, configParamMinimumAppVersion }) => {
  const [latestRelease, setLatestRelease] = useState<string>(configParamLatestAvailableRelease?.value || '')
  const [minimumAppVersion, setMinimumAppVersion] = useState<string>(configParamMinimumAppVersion?.value || '')
  const [isEditingMinVersion, setIsEditingMinVersion] = useState<boolean>(false)
  const [draftMinVersion, setDraftMinVersion] = useState<string>(configParamMinimumAppVersion?.value || '')
  const addMessage = useAddPopupMessage()
  const { openDialog: openConfirmDialog, closeDialog: closeConfirmDialog } = useDialog(DialogNames.ConfirmMinimumAppVersion)
  const editAvailableReleaseRequest = useEditConfigurationParamRequest({ id: configParamLatestAvailableRelease?.id || 0 })
  const editMinimumAppVersionRequest = useEditConfigurationParamRequest({ id: configParamMinimumAppVersion?.id || 0 })
  const minVersionItemFactory = useDropdownItemFactory({ params: { labelProp: 'label', valueProp: 'number' } })
  const releaseItemFactory = useDropdownItemFactory({ params: { labelProp: 'number', valueProp: 'number' } })

  const updateAvailableRelease = (value: string) => {
    const isExistValue = releases.find((release) => release.number === value)
    if (!isExistValue) return addMessage({ type: 'error', text: 'Release not found' })

    const _latestRelease = latestRelease
    setLatestRelease(value)
    editAvailableReleaseRequest.mutate(
      { value },
      {
        onSuccess: () => addMessage({ type: 'success', text: 'Updated successfully' }),
        onError: (error) => {
          addMessage({ type: 'error', text: error.message })
          setLatestRelease(_latestRelease)
        }
      }
    )
  }

  const saveMinimumAppVersion = (value: string) => {
    if (value !== '' && !releases.find((release) => release.number === value)) {
      return addMessage({ type: 'error', text: 'Release not found' })
    }

    const _minimumAppVersion = minimumAppVersion
    setMinimumAppVersion(value)
    editMinimumAppVersionRequest.mutate(
      { value },
      {
        onSuccess: () => {
          setIsEditingMinVersion(false)
          addMessage({ type: 'success', text: 'Updated successfully' })
        },
        onError: (error) => {
          addMessage({ type: 'error', text: error.message })
          setMinimumAppVersion(_minimumAppVersion)
        }
      }
    )
  }

  const startEditMinVersion = () => {
    setDraftMinVersion(minimumAppVersion)
    setIsEditingMinVersion(true)
  }
  const cancelEditMinVersion = () => {
    setDraftMinVersion(minimumAppVersion)
    setIsEditingMinVersion(false)
  }
  const handleSaveMinVersion = () => {
    // No change — just leave edit mode.
    if (draftMinVersion === minimumAppVersion) return setIsEditingMinVersion(false)
    // Clearing the minimum unblocks everyone, so no warning is needed.
    if (draftMinVersion === '') return saveMinimumAppVersion('')
    // Raising the minimum can lock out users — confirm first.
    return openConfirmDialog()
  }
  const confirmMinVersion = () => {
    closeConfirmDialog()
    saveMinimumAppVersion(draftMinVersion)
  }

  const minimumAppVersionOptions = useMemo(() => {
    const options: { number: string; label: string }[] = [{ number: '', label: 'None' }]
    releases.forEach((release) => {
      options.push({ number: release.number, label: release.number })
    })
    return options
  }, [releases])

  return (
    <Grid container direction="column" alignItems="stretch">
      <ListHeader title="What’s new release notes">
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <Box position="relative" width="220px">
              <Dropdown
                list={minimumAppVersionOptions}
                name="minimumAppVersion"
                label="Minimum app version"
                value={isEditingMinVersion ? draftMinVersion : minimumAppVersion}
                renderValue={(value) => value || 'None'}
                SelectProps={{
                  displayEmpty: true,
                  disabled: !isEditingMinVersion,
                  sx: {
                    '& .MuiSelect-select': { pr: '76px !important' },
                    '& .MuiSelect-icon': { display: 'none' }
                  }
                }}
                menuItemFactory={minVersionItemFactory}
                onChange={(_, value) => setDraftMinVersion(value)}
                onBlur={(_, value) => setDraftMinVersion(value)}
              />
              <Box position="absolute" top="50%" right={8} sx={{ transform: 'translateY(-50%)', display: 'flex' }}>
                {isEditingMinVersion ? (
                  <>
                    <IconButton size="small" color="primary" disabled={editMinimumAppVersionRequest.isLoading} onClick={handleSaveMinVersion}>
                      <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={cancelEditMinVersion}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  <IconButton size="small" color="primary" onClick={startEditMinVersion}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
          </Grid>
          <Grid item width="200px">
            <Dropdown
              list={releases}
              name="release"
              label="Latest available release"
              value={latestRelease}
              menuItemFactory={releaseItemFactory}
              onChange={(_, value) => updateAvailableRelease(value)}
              onBlur={(_, value) => updateAvailableRelease(value)}
            />
          </Grid>
          <Grid item>
            <Link to="/releases/new" style={{ textDecoration: 'none' }}>
              <Button role="link" variant="contained">
                Add new release
              </Button>
            </Link>
          </Grid>
        </Grid>
      </ListHeader>
      <Grid container flexDirection="column" mt={2}>
        {releases.map((release) => (
          <ReleaseCard release={release} key={release.id} />
        ))}
      </Grid>

      <StatefulDialog
        name={DialogNames.ConfirmMinimumAppVersion}
        actions={
          <DialogActions>
            <Button variant="outlined" onClick={() => closeConfirmDialog()}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={confirmMinVersion} disabled={editMinimumAppVersionRequest.isLoading}>
              Confirm
            </Button>
          </DialogActions>
        }
      >
        <Box minWidth="320px">
          <Typography>
            Be careful: people using a Climit app version lower than {draftMinVersion} won’t be able to proceed until they update.
          </Typography>
        </Box>
      </StatefulDialog>
    </Grid>
  )
}

const ReleaseListLoader: FC = () => {
  document.title = 'CLīMIT Admin Panel - Releases'
  const { isInitialLoading, isError, error, data } = useReleaseListRequest({
    params: { limit: 100, sort: 'id:desc' }
  })
  const { data: configListData } = useConfigurationListRequest({
    params: { limit: 100, sort: 'id:desc' }
  })
  const releases = data || []
  const latestAvailableRelease = useMemo(() => (configListData || []).find((config) => config.type === 'latest_available_release'), [configListData])
  const minimumAppVersion = useMemo(() => (configListData || []).find((config) => config.type === 'minimum_app_version'), [configListData])

  return (
    <HasSidebarLayout>
      <DataLoad isLoading={isInitialLoading} isError={isError} errorMessage={error?.message || ''}>
        <ReleaseList releases={releases} configParamLatestAvailableRelease={latestAvailableRelease} configParamMinimumAppVersion={minimumAppVersion} />
      </DataLoad>
    </HasSidebarLayout>
  )
}

export default ReleaseListLoader
